import { useSelector } from 'react-redux'
import * as R from 'ramda'
import * as Sentry from 'mage-deps/@sentry/browser'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'

import { hooks as i18nHooks } from 'mage-i18n'
import { Selector, MultiSelector } from 'mage-deps/@klarna/mp-ui'
import { selectors as coreSelectors } from 'mage-core'

const sortAlphabetically = R.sort((a, b) => a.localeCompare(b))
const sortAlphabeticallyByLabel = R.sortBy(R.prop('label'))

const generateDefaultOptions = R.pipe(
  R.map(mid => ({ value: mid, label: mid })),
  sortAlphabeticallyByLabel
)

const getMidData = R.memoizeWith(
  (_, mids) => R.pipe(sortAlphabetically, R.join(','))(mids),
  (client, mids) => client.request({
    method: 'GET',
    url: '/mids/',
    params: {
      mid: mids.length > 1 ? mids : mids[0]
    }
  })
)

export const mapMidsToOptions = async (client, mids) => {
  if (!Array.isArray(mids) || !mids.length) return []

  const data = []
  try {
    const batchSize = 140
    // We don't want to make more than 10 requests
    if (mids.length <= batchSize * 10) {
      const batches = R.splitEvery(batchSize, mids)
      for (const batch of batches) {
        const response = await getMidData(client, batch)
        const midData = R.propOr([], 'data', response)
        data.push(...midData)
      }
    }
  } catch (e) {
    Sentry.captureException(e)
    return []
  }

  const options = mids.map(mid => {
    const midData = data.find(R.propEq('id', mid))
    const brandName = R.prop('brand_name', midData)

    if (brandName) {
      return {
        description: mid,
        label: brandName,
        value: mid
      }
    } else {
      return {
        label: mid,
        value: mid
      }
    }
  })

  return sortAlphabeticallyByLabel(options)
}

export const StoreSelector = ({
  borderless,
  children,
  // NOTE: Passing custom client for testing purposes
  client,
  closeText,
  closeTextWithSelection,
  deselectAllOptionText,
  disabled,
  emptyStateText,
  fullWidthTrigger,
  id,
  isMultiselect,
  label,
  loading,
  maxHeight,
  maxWidth,
  mids,
  onChange,
  onClose,
  searchFieldLabel,
  selectAllOptionText,
  selectedAllText,
  selectedText,
  value,
  values
}) => {
  const [options, setOptions] = useState(generateDefaultOptions(mids))

  const defaultClient = useSelector(state => coreSelectors.clientFactory(state)('/api/users'))
  const t = i18nHooks.useTranslator()

  useEffect(() => {
    const updateOptions = async () => {
      const updatedOptions = await mapMidsToOptions(client || defaultClient, mids)
      setOptions(updatedOptions)
    }
    updateOptions()
  }, [mids])

  return (
    <>
      {isMultiselect
        ? (
          <MultiSelector
            borderless={borderless}
            closeText={closeText || t('core.storesSelector.closeText')}
            closeTextWithSelection={closeTextWithSelection || t('core.storesSelector.closeTextWithSelection')}
            deselectAllOptionText={deselectAllOptionText || t('core.storesSelector.deselectAllOptionText')}
            disabled={disabled}
            emptyStateText={emptyStateText || t('core.storesSelector.emptyStateText')}
            fullWidthTrigger={fullWidthTrigger}
            id={id}
            label={label || t('core.storesSelector.label')}
            loading={loading}
            maxHeight={maxHeight}
            maxWidth={maxWidth}
            onChange={onChange}
            onClose={onClose}
            options={options}
            searchFieldLabel={searchFieldLabel || t('core.storesSelector.searchFieldLabel')}
            selectAllOptionText={selectAllOptionText || t('core.storesSelector.selectAllOptionText')}
            selectedAllText={selectedAllText || t('core.storesSelector.selectedAllText')}
            selectedText={selectedText || t('core.storesSelector.selectedText')}
            values={values || []}
            withSearch={mids.length > 4}
            withSelectAll={mids.length > 1}
          >
            {children}
          </MultiSelector>
        )
        : (
          <Selector
            borderless={borderless}
            closeText={closeText || t('core.storesSelector.closeText')}
            disabled={disabled}
            fullWidthTrigger={fullWidthTrigger}
            id={id}
            label={label || t('core.storeSelector.label')}
            loading={loading}
            maxHeight={maxHeight}
            maxWidth={maxWidth}
            onChange={onChange}
            onClose={onClose}
            options={options}
            value={value || ''}
          >
            {children}
          </Selector>
        )}
    </>
  )
}

StoreSelector.propTypes = {
  borderless: PropTypes.bool,
  children: PropTypes.node,
  // NOTE: Passing custom client for testing purposes
  client: PropTypes.object,
  closeText: PropTypes.string,
  closeTextWithSelection: PropTypes.string,
  deselectAllOptionText: PropTypes.string,
  disabled: PropTypes.bool,
  emptyStateText: PropTypes.string,
  fullWidthTrigger: PropTypes.bool,
  id: PropTypes.string,
  isMultiselect: PropTypes.bool,
  label: PropTypes.string,
  loading: PropTypes.bool,
  maxHeight: PropTypes.number,
  maxWidth: PropTypes.number,
  mids: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func,
  onClose: PropTypes.func,
  searchFieldLabel: PropTypes.string,
  selectAllOptionText: PropTypes.string,
  selectedAllText: PropTypes.string,
  selectedText: PropTypes.string,
  value: PropTypes.string,
  values: PropTypes.arrayOf(PropTypes.string)
}

StoreSelector.defaultProps = {
  mids: [],
  onChange: () => null,
  onClose: () => null
}

export default StoreSelector
